// DESCRIPTION: Day view — single-day time-slot grid with absolutely positioned event blocks.

import React, { useState } from 'react';
import EventPopover from './EventPopover';
import EventCell from './EventCell';
import {
	formatHourLabel,
	formatTime,
	getDisplayTime,
	computeOverlapColumns,
} from './utils';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

const HOURS = Array.from( { length: 24 }, ( _, i ) => i );
const CELL_HEIGHT_PX = 56;

function getPositionStyle( time, durationMinutes ) {
	if ( ! time ) {
		return {};
	}
	const [ h, m ] = time.split( ':' ).map( Number );
	const startMin = h * 60 + ( m || 0 );
	const topPx = ( startMin / 60 ) * CELL_HEIGHT_PX;
	const heightPx = Math.max(
		( ( durationMinutes || 60 ) / 60 ) * CELL_HEIGHT_PX,
		20
	);
	return { top: `${ topPx }px`, height: `${ heightPx }px` };
}

/**
 * DayView
 *
 * @param {Object}  props
 * @param {Array}   props.events     All events in the requested range (already filtered to this day by API).
 * @param {Date}    props.anchor     The day being displayed.
 * @param {boolean} props.useLocalTz Whether to show times in browser's local timezone.
 */
export default function DayView( { events, anchor, useLocalTz } ) {
	const [ activeEvent, setActiveEvent ] = useState( null );

	const dateStr = anchor.toISOString().slice( 0, 10 );
	const dayEvents = events.filter(
		( e ) => ( e.date_utc || e.date || '' ) === dateStr
	);
	const allDayEvents = dayEvents.filter( ( e ) => ! e.time );
	const timedEvents = dayEvents.filter( ( e ) => !! e.time );
	const layoutEvents = computeOverlapColumns( timedEvents );
	const totalHeight = HOURS.length * CELL_HEIGHT_PX;

	return (
		<div>
			{ /* All-day banner row */ }
			{ allDayEvents.length > 0 && (
				<div className="nb-calendar-all-day-row">
					<div className="nb-calendar-all-day-label">All day</div>
					<div className="nb-calendar-all-day-cells">
						<div className="nb-calendar-all-day-cell">
							{ allDayEvents.map( ( event ) => (
								<EventCell
									key={ event.id }
									event={ event }
									isAllDay
									useLocalTz={ useLocalTz }
								/>
							) ) }
						</div>
					</div>
				</div>
			) }

			<div
				className="nb-calendar-time-grid"
				style={ { minHeight: '400px' } }
			>
				{ /* Time gutter */ }
				<div className="nb-calendar-time-gutter">
					{ HOURS.map( ( h ) => (
						<div
							key={ h }
							className="nb-calendar-time-label"
							style={ { height: `${ CELL_HEIGHT_PX }px` } }
						>
							{ formatHourLabel( h ) }
						</div>
					) ) }
				</div>

				{ /* Single day column */ }
				<div className="nb-calendar-time-columns">
					<div className="nb-calendar-time-column">
						<div
							style={ {
								position: 'relative',
								height: `${ totalHeight }px`,
							} }
						>
							{ HOURS.map( ( h ) => (
								<div
									key={ h }
									className="nb-calendar-time-slot"
									style={ {
										height: `${ CELL_HEIGHT_PX }px`,
									} }
								/>
							) ) }

							{ layoutEvents.map(
								( { event, column, totalColumns } ) => {
									const posStyle = getPositionStyle(
										event.time,
										event.duration
									);
									const widthPct = 100 / totalColumns;
									const leftPct = column * widthPct;

									return (
										<button
											key={ event.id }
											className={ `nb-calendar-time-event${
												event.is_past
													? ' nb-calendar-time-event--past'
													: ''
											}` }
											style={ {
												...posStyle,
												left: `calc(${ leftPct }% + 2px)`,
												width: `calc(${ widthPct }% - 4px)`,
												right: 'auto',
											} }
											onClick={ () =>
												setActiveEvent(
													activeEvent?.id === event.id
														? null
														: event
												)
											}
											aria-label={ event.title }
										>
											<div className="nb-calendar-time-event__title">
												{ event.title }
											</div>
											{ config.showEventTime !== false &&
												event.time && (
													<div className="nb-calendar-time-event__time">
														{ formatTime(
															getDisplayTime(
																event,
																useLocalTz
															)
														) }
													</div>
												) }
											{ event.location && (
												<div className="nb-calendar-time-event__time">
													📍 { event.location }
												</div>
											) }
											{ activeEvent?.id === event.id && (
												<EventPopover
													event={ event }
													useLocalTz={ useLocalTz }
													onClose={ () =>
														setActiveEvent( null )
													}
												/>
											) }
										</button>
									);
								}
							) }
						</div>
					</div>
				</div>

				{ dayEvents.length === 0 && (
					<p
						className="nb-calendar-error"
						style={ { width: '100%' } }
					>
						{ strings.noEvents || 'No events this period.' }
					</p>
				) }
			</div>
		</div>
	);
}
